/**
 * Asks the person to sign in again before a passkey is added or removed.
 *
 * The server only lets a session change passkeys within a few minutes of a
 * fresh sign-in. This runs the portal's own sign-in popup with `reauth=1`,
 * which makes the identity provider ask for the password (or equivalent)
 * again. When the popup reports success the portal has refreshed its token
 * onto the new session, and `onSignedIn` lets the caller carry on: a removal
 * retries at once; adding a passkey waits for a click, because the browser
 * prompt needs one.
 *
 * Only this prompt's own, live popup can call `onSignedIn`. Cancelling or
 * leaving the prompt stops listening, so a sign-in finished later in a popup
 * left open cannot remove a passkey the person decided to keep.
 */
import { Box, Flex, Icon, Stack, Text } from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { TbUserCheck } from "react-icons/tb";

import PloyButton from "components/ds/PloyButton";
import { getSubdomain } from "modules/members/api";
import {
  offeredProviders,
  type LoginProviderOption,
} from "pages/Members/Auth/loginProviders";
import { openSignInWindow, signInRequestPath } from "pages/Members/Auth/oauth";
import type { LoginProvider } from "state/members/member";
import MFMembers from "state/members/membersState";

export const REAUTH_FAILED_MESSAGE =
  "We could not finish signing you in. Choose a sign-in option to try again.";

export type ReauthPromptProps = {
  /** "To add a passkey, confirm it's you by signing in again." */
  message: string;
  /** Called once this prompt's sign-in popup reports success. */
  onSignedIn: () => void;
  onCancel?: () => void;
  /** Shown when signing in again did not satisfy the server last time. */
  stillRequired?: boolean;
};

export type ReauthStatus = "idle" | "waiting" | "failed";

export const ReauthPromptView = ({
  message,
  providers,
  status,
  stillRequired,
  onProvider,
  onCancel,
}: {
  message: string;
  providers: LoginProviderOption[];
  status: ReauthStatus;
  stillRequired?: boolean;
  onProvider: (provider: LoginProvider) => void;
  onCancel?: () => void;
}) => (
  <Stack
    spacing="md"
    bg="neutral.50"
    borderWidth="1px"
    borderColor="neutral.200"
    borderRadius="component.xl"
    p="lg"
  >
    <Flex gap="sm" align="flex-start">
      <Box as={TbUserCheck} boxSize="18px" color="neutral.700" mt="2px" aria-hidden />
      <Text fontSize="smd" color="neutral.900">
        {message}
      </Text>
    </Flex>

    {stillRequired && (
      <Text role="alert" fontSize="smd" color="warning.800">
        We still could not confirm it's you. If this keeps happening, ask your
        IT team: your sign-in provider may not be set up for this yet.
      </Text>
    )}

    {status === "waiting" && (
      <Text fontSize="smd" color="neutral.700">
        Complete the sign-in in the window that opened. If you closed it,
        choose a sign-in option again.
      </Text>
    )}

    {status === "failed" && (
      <Text role="alert" fontSize="smd" color="error.700">
        {REAUTH_FAILED_MESSAGE}
      </Text>
    )}

    <Flex gap="sm" wrap="wrap">
      {providers.map((provider) => (
        <PloyButton
          key={provider.id}
          variant="secondary"
          size="md"
          leftIcon={<Icon as={provider.icon} boxSize="14px" />}
          onClick={() => onProvider(provider.id)}
        >
          Sign in with {provider.name}
        </PloyButton>
      ))}
      {onCancel && (
        <PloyButton variant="ghost" size="md" onClick={onCancel}>
          Cancel
        </PloyButton>
      )}
    </Flex>

    {providers.length === 0 && (
      <Text fontSize="smd" color="neutral.700">
        Sign out and sign back in, then try again.
      </Text>
    )}
  </Stack>
);

const ReauthPrompt = ({
  message,
  onSignedIn,
  onCancel,
  stillRequired,
}: ReauthPromptProps) => {
  const config = MFMembers.useGetPortalConfig();
  const [status, setStatus] = useState<ReauthStatus>("idle");
  const stopListening = useRef<(() => void) | null>(null);
  const offered = offeredProviders(
    config?.loginOptions ?? config?.availableLoginOptions ?? [],
  );

  const stop = () => {
    stopListening.current?.();
    stopListening.current = null;
  };

  // Leaving the prompt (cancel, a route change) ends its popup's say.
  useEffect(() => stop, []);

  const onProvider = (provider: LoginProvider) => {
    setStatus("waiting");
    stop();
    stopListening.current = openSignInWindow(
      signInRequestPath(provider, getSubdomain(), { reauth: true }),
      "Reauthenticate",
      () => {
        stopListening.current = null;
        setStatus("idle");
        onSignedIn();
      },
      () => {
        stopListening.current = null;
        setStatus("failed");
      },
    );
  };

  return (
    <ReauthPromptView
      message={message}
      providers={offered}
      status={status}
      stillRequired={stillRequired}
      onProvider={onProvider}
      onCancel={
        onCancel &&
        (() => {
          stop();
          onCancel();
        })
      }
    />
  );
};

export default ReauthPrompt;
